import React, { createContext, useContext, ReactNode } from "react";
import {
  ContractsV2EnhancedProps,
  DrawerComponentSlots,
  UsageBalanceHookRegistry,
} from "../canonical";
import { Site, SiteContext } from "../canonical";

interface ContractsContextType {
  useGetAccounts: ContractsV2EnhancedProps["useGetAccounts"];
  useGetContractData: ContractsV2EnhancedProps["useGetContractData"];
  useGetDetailedRetainerExpenseByContractId: ContractsV2EnhancedProps["useGetDetailedRetainerExpenseByContractId"];
  useGetRecurringServicesContractDetailByContractId: ContractsV2EnhancedProps["useGetRecurringServicesContractDetailByContractId"];
  useContractRecordsDetailFields: ContractsV2EnhancedProps["useContractRecordsDetailFields"];
  userPrefHook: ContractsV2EnhancedProps["userPrefHook"];
  siteContext: SiteContext;
  componentSlots?: DrawerComponentSlots;
  usageBalanceHookRegistry?: UsageBalanceHookRegistry;
  isValidShi?: boolean;
}

const ContractsContext = createContext<ContractsContextType | undefined>(
  undefined,
);

interface ContractsProviderProps extends ContractsV2EnhancedProps {
  children: ReactNode;
}

export function ContractsProvider({
  children,
  useGetAccounts,
  useGetContractData,
  useGetDetailedRetainerExpenseByContractId,
  useGetRecurringServicesContractDetailByContractId,
  useContractRecordsDetailFields,
  userPrefHook,
  siteContext = Site.CustomerExperience,
  componentSlots,
  usageBalanceHookRegistry,
  isValidShi,
}: ContractsProviderProps) {
  return (
    <ContractsContext.Provider
      value={{
        useGetAccounts,
        useGetContractData,
        useGetDetailedRetainerExpenseByContractId,
        useGetRecurringServicesContractDetailByContractId,
        useContractRecordsDetailFields,
        userPrefHook,
        siteContext,
        componentSlots,
        usageBalanceHookRegistry,
        isValidShi,
      }}
    >
      {children}
    </ContractsContext.Provider>
  );
}

export function useContractsContext() {
  const context = useContext(ContractsContext);
  if (context === undefined) {
    throw new Error(
      "useContractsContext must be used within a ContractsProvider",
    );
  }
  return context;
}

export function useOptionalContractsContext() {
  return useContext(ContractsContext);
}
